import { Fragment, type ReactNode } from 'react'
import { storeHref } from './storefront-chat-api'

// The store chat's markdown subset (shopify-theme-v2 frontend/lib/chat-markdown.tsx): paragraphs,
// lists, bold, italic, inline code and links, built as React elements. Links open the store.

type Block = { kind: 'paragraph'; lines: string[] } | { kind: 'list'; ordered: boolean; items: string[] }

const LIST_ITEM = /^\s*(?:([-*•])|(\d+)[.)])\s+(.*)$/
const INLINE =
  /(\*\*([^*]+)\*\*|__([^_]+)__|\*([^*\s][^*]*)\*|_([^_\s][^_]*)_|`([^`]+)`|\[([^\]]+)\]\(([^)\s]+)\)|https?:\/\/[^\s<>()]+[^\s<>().,;:!?'"])/g

function renderInline(text: string, keyPrefix: string): ReactNode[] {
  const nodes: ReactNode[] = []
  let cursor = 0
  let index = 0
  for (const match of text.matchAll(INLINE)) {
    const start = match.index
    if (start > cursor) nodes.push(text.slice(cursor, start))
    const key = `${keyPrefix}-${index++}`
    const [whole, , bold1, bold2, italic1, italic2, code, linkText, linkHref] = match
    const bold = bold1 ?? bold2
    const italic = italic1 ?? italic2
    if (bold) nodes.push(<strong key={key}>{renderInline(bold, key)}</strong>)
    else if (italic) nodes.push(<em key={key}>{renderInline(italic, key)}</em>)
    else if (code) nodes.push(<code key={key}>{code}</code>)
    else {
      const href = storeHref(linkHref ?? whole)
      const label = linkText ? renderInline(linkText, key) : whole
      nodes.push(
        href ? (
          // eslint-disable-next-line no-restricted-syntax -- external storefront URL
          <a key={key} href={href} target="_blank" rel="noopener noreferrer">
            {label}
          </a>
        ) : (
          <Fragment key={key}>{label}</Fragment>
        ),
      )
    }
    cursor = start + whole.length
  }
  if (cursor < text.length) nodes.push(text.slice(cursor))
  return nodes
}

function parseBlocks(source: string): Block[] {
  const blocks: Block[] = []
  let current: Block | null = null
  for (const line of source.replace(/\r\n?/g, '\n').split('\n')) {
    if (!line.trim()) {
      current = null
      continue
    }
    const item = LIST_ITEM.exec(line)
    if (item) {
      const ordered = Boolean(item[2])
      if (current?.kind !== 'list' || current.ordered !== ordered) {
        current = { kind: 'list', ordered, items: [] }
        blocks.push(current)
      }
      current.items.push(item[3] ?? '')
      continue
    }
    if (current?.kind === 'list') {
      // An indented line continues the previous list item.
      if (/^\s+/.test(line) && current.items.length) {
        current.items[current.items.length - 1] += ` ${line.trim()}`
        continue
      }
      current = null
    }
    const heading = /^#{1,6}\s+(.*)$/.exec(line.trim())
    const text = heading ? `**${heading[1]}**` : line.trim()
    if (current?.kind !== 'paragraph') {
      current = { kind: 'paragraph', lines: [] }
      blocks.push(current)
    }
    current.lines.push(text)
  }
  return blocks
}

export function ChatMarkdown({ source }: { source: string }) {
  return (
    <>
      {parseBlocks(source).map((block, blockIndex) => {
        const key = `b${blockIndex}`
        if (block.kind === 'list') {
          const List = block.ordered ? 'ol' : 'ul'
          return (
            <List key={key}>
              {block.items.map((item, itemIndex) => (
                <li key={`${key}-${itemIndex}`}>{renderInline(item, `${key}-${itemIndex}`)}</li>
              ))}
            </List>
          )
        }
        return (
          <p key={key}>
            {block.lines.map((line, lineIndex) => (
              <Fragment key={`${key}-${lineIndex}`}>
                {lineIndex > 0 && <br />}
                {renderInline(line, `${key}-${lineIndex}`)}
              </Fragment>
            ))}
          </p>
        )
      })}
    </>
  )
}
